import { getSsoPartner, ssoLogoutUrl } from '@/features/auth/ssoPartners';
import { AUTH_ROUTES } from '@/lib/constants';
import { isStaffRole } from '@/lib/rbac';
import type { User } from '@/types/auth';

/**
 * Decide which login route to send a user to after logout.
 *
 * The **current pathname is the primary signal** — it's the most reliable
 * indicator of which surface the user is on, and crucially it survives
 * `clearAuth()` racing with concurrent 401s. The earlier role-based version
 * had a footgun: when several in-flight queries all returned 401 at access-
 * token expiry, the first 401 wiped the store; subsequent 401s read
 * `user: null` and fell through to client login, so a staff user mid-session
 * could land on `/client/login` even though their last URL was `/admin/...`.
 *
 *   /client + /client/...  → `/client/login`
 *   /admin, /app, /login,  → `/login`
 *   everything else        → `/login` (safer default than client login —
 *                             staff routes are deeper, and any client who
 *                             lands on `/login` can still reach
 *                             `/client/login` from there)
 *
 * Pathname is optional so tests and non-browser contexts can drive purely
 * from `user.role`; in the browser we default to `window.location.pathname`.
 */
export function resolveLogoutDestination(user: User | null, pathname?: string): string {
  // SSO first, and ahead of the pathname check: a federated student has no
  // password, so /client/login is a dead end for them whatever page they were
  // on. This returns an absolute BE URL (not a route) because the Cognito
  // session has to die too, or the partner button signs them back in silently.
  // An unrecognised slug falls through to the password form rather than
  // stranding the user on the "Unknown sign-in link" page.
  if (user?.ssoProvider && getSsoPartner(user.ssoProvider)) return ssoLogoutUrl(user.ssoProvider);

  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (p === '/client' || p.startsWith('/client/')) return AUTH_ROUTES.CLIENT_LOGIN;
  // Non-client pathname: prefer role when available (e.g. tests pass user
  // without a pathname). `client` role on a non-client pathname is an
  // anomaly we tolerate by trusting role.
  if (user?.role === 'client') return AUTH_ROUTES.CLIENT_LOGIN;
  if (user && isStaffRole(user.role)) return AUTH_ROUTES.STAFF_LOGIN;
  return AUTH_ROUTES.STAFF_LOGIN;
}
